import React, { useEffect, useState, useRef } from 'react';
import VolumeIndicator from './index';

/**
 * 与录音系统集成的音量指示器
 * 可以直接接收录音的 powerLevel 并显示，也可以通过传入的 powerLevel prop 直接控制
 */
const RecorderVolumeIndicator = ({ 
    recorder = null, 
    isRecording = false,
    powerLevel = null,  // 新增：直接传入音量级别
    barCount = 20,
    width = 400,
    height = 50,
    sensitivity = 1.0, // 灵敏度调节
    smoothing = 0.3     // 平滑系数
}) => {
    const [currentPowerLevel, setCurrentPowerLevel] = useState(0);
    const [smoothedLevel, setSmoothedLevel] = useState(0);
    const animationFrameRef = useRef();
    const lastUpdateTime = useRef(Date.now());

    // 平滑处理函数
    const smoothPowerLevel = (newLevel, oldLevel, factor) => {
        return oldLevel + (newLevel - oldLevel) * factor;
    };

    // 处理录音数据的回调函数
    const handleRecordingProcess = React.useCallback((buffers, powerLevelFromRecorder, bufferDuration, bufferSampleRate) => {
        if (!isRecording) return;
        
        // 调整灵敏度
        let adjustedLevel = powerLevelFromRecorder * sensitivity;
        
        // 限制范围在 0-100
        adjustedLevel = Math.max(0, Math.min(100, adjustedLevel));
        
        setCurrentPowerLevel(adjustedLevel);
    }, [isRecording, sensitivity]);

    // 处理直接传入的 powerLevel
    useEffect(() => {
        if (powerLevel !== null) {
            let adjustedLevel = powerLevel * sensitivity;
            adjustedLevel = Math.max(0, Math.min(100, adjustedLevel));
            setCurrentPowerLevel(adjustedLevel);
        }
    }, [powerLevel, sensitivity]);

    // 平滑动画循环
    useEffect(() => {
        const animate = () => {
            const now = Date.now();
            const deltaTime = now - lastUpdateTime.current;
            
            if (deltaTime > 16) { // 约60fps
                setSmoothedLevel(prev => {
                    const target = isRecording ? currentPowerLevel : 0;
                    return smoothPowerLevel(target, prev, smoothing);
                });
                lastUpdateTime.current = now;
            }
            
            animationFrameRef.current = requestAnimationFrame(animate);
        };
        
        animationFrameRef.current = requestAnimationFrame(animate);
        
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [currentPowerLevel, isRecording, smoothing]);

    // 监听录音状态变化
    useEffect(() => {
        if (!recorder) return;

        // 如果录音器有 onProcess 回调，我们需要包装它
        const originalOnProcess = recorder.set?.onProcess;
        
        if (recorder.set) {
            recorder.set.onProcess = (buffers, powerLevel, bufferDuration, bufferSampleRate) => {
                // 调用原始回调
                if (originalOnProcess) {
                    originalOnProcess(buffers, powerLevel, bufferDuration, bufferSampleRate);
                }
                // 处理我们的音量显示
                handleRecordingProcess(buffers, powerLevel, bufferDuration, bufferSampleRate);
            };
        }

        return () => {
            // 恢复原始回调
            if (recorder.set && originalOnProcess) {
                recorder.set.onProcess = originalOnProcess;
            }
        };
    }, [recorder, handleRecordingProcess]);

    return (
        <div className="recorder-volume-indicator">
            <VolumeIndicator
                powerLevel={smoothedLevel}
                barCount={barCount}
                width={width}
                height={height}
            />
            
        </div>
    );
};

export default RecorderVolumeIndicator;
